import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getRealtimeRevenue,
  getRealtimeActiveMembers,
  getSalesSummary,
  getSalesDetail,
  getTargetData,
  postTargetData,
  patchTargetData,
  getMemberSummary,
  getMemberDetail,
  type RealtimeParams,
  type MonthlyParams,
  type SalesDetailParams,
  type MemberSummaryParams,
  type MemberMonthlyParams,
  type PostTargetDataPayload,
} from "api/businessDashboard";

// --- Query Keys ---
export const dashboardQueryKeys = {
  all: ["dashboard"] as const,
  realtimeRevenue: (params: RealtimeParams) =>
    [...dashboardQueryKeys.all, "realtimeRevenue", params] as const,
  realtimeMembers: (params: RealtimeParams) =>
    [...dashboardQueryKeys.all, "realtimeMembers", params] as const,
  salesSummary: (params: MonthlyParams) =>
    [...dashboardQueryKeys.all, "salesSummary", params] as const,
  salesDetail: (params: SalesDetailParams) =>
    [...dashboardQueryKeys.all, "salesDetail", params] as const,
  targetData: (params: MonthlyParams) =>
    [...dashboardQueryKeys.all, "targetData", params] as const,
  memberSummary: (params: MemberSummaryParams) =>
    [...dashboardQueryKeys.all, "memberSummary", params] as const,
  memberDetail: (params: MemberMonthlyParams) =>
    [...dashboardQueryKeys.all, "memberDetail", params] as const,
};

// --- 실시간 현황 훅 ---
export const useRealtimeRevenueQuery = (params: RealtimeParams) => {
  return useQuery({
    queryKey: dashboardQueryKeys.realtimeRevenue(params),
    queryFn: () => getRealtimeRevenue(params),
    enabled: !!params?.seqPartner,
  });
};

export const useRealtimeMembersQuery = (params: RealtimeParams) => {
  return useQuery({
    queryKey: dashboardQueryKeys.realtimeMembers(params),
    queryFn: () => getRealtimeActiveMembers(params),
    enabled: !!params?.seqPartner,
  });
};

// --- 매출 분석 훅 ---
export const useSalesSummaryQuery = (params: MonthlyParams) => {
  return useQuery({
    queryKey: dashboardQueryKeys.salesSummary(params),
    queryFn: () => getSalesSummary(params),
    enabled: !!params.referenceMonth && !!params.seqPartner,
    staleTime: 30 * 1000,
  });
};

export const useSalesDetailQuery = (params: SalesDetailParams) => {
  return useQuery({
    queryKey: dashboardQueryKeys.salesDetail(params),
    queryFn: () => getSalesDetail(params),
    enabled: !!params.referenceMonth && !!params.seqPartner,
    staleTime: 30 * 1000,
  });
};

// BEP & 목표 매출 관련 훅
export const useTargetDataQuery = (
  params: MonthlyParams,
  options: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: dashboardQueryKeys.targetData(params),
    queryFn: () => getTargetData(params),
    enabled: options?.enabled && !!params.referenceMonth && !!params.seqPartner,
    staleTime: 30 * 1000,
  });
};

export const usePostTargetDataMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PostTargetDataPayload) => postTargetData(payload),
    onSuccess: (_, variables) => {
      // 성공 시 관련 쿼리 무효화하여 최신 데이터 다시 불러오기
      queryClient.invalidateQueries({
        queryKey: dashboardQueryKeys.targetData({
          referenceMonth: variables.referenceMonth,
        }),
      });
    },
  });
};

export const usePatchTargetDataMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PostTargetDataPayload) => postTargetData(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: dashboardQueryKeys.targetData({
          referenceMonth: variables.referenceMonth,
        }),
      });
    },
  });
};

// --- 회원 분석 훅 ---
export const useMemberSummaryQuery = (params: MemberSummaryParams) => {
  return useQuery({
    queryKey: dashboardQueryKeys.memberSummary(params),
    queryFn: () => getMemberSummary(params),
    enabled:
      !!params.referenceStartMonth &&
      !!params.referenceEndMonth &&
      !!params.passType,
    staleTime: 30 * 1000,
  });
};

export const useMemberDetailQuery = (params: MemberMonthlyParams) => {
  return useQuery({
    queryKey: dashboardQueryKeys.memberDetail(params),
    queryFn: () => getMemberDetail(params),
    enabled: !!params.referenceStartMonth && !!params.referenceEndMonth,
    staleTime: 30 * 1000,
  });
};
